import React, { memo } from "react";
import PropTypes from "prop-types";
import Image from "../Image";

const TabPanel = ({
  img,
  imageClassName,
  contentClassName,
  heading,
  headingClass,
  body,
  footer,
  alignStart,
  mobileVisibleHeading,
  headingType,
  isVideo,
}) => {
  const headingClassNames = `panel-heading text-3md md:text-shs font-semibold text-cf-hero max-w-[500px] ${headingClass} font-body text-[#240253] ${
    mobileVisibleHeading ? "block" : "hidden md:block"
  }`;

  const safariAgent =
    typeof window !== "undefined" &&
    /Safari/.test(navigator.userAgent) &&
    !/Chrome/.test(navigator.userAgent);

  const renderMedia = () => {
    if (!img?.src && !img?.mp4Src) {
      return null; // Avoid rendering if both src and mp4Src are missing
    }

    if (isVideo) {
      return (
        <video
          autoPlay
          loop
          muted
          playsInline
          width={img?.width || "100%"}
          height={img?.height || "auto"}
          className={imageClassName}
          onError={(e) => {
            e.target.onerror = null; // Prevent infinite loop
            e.target.style.display = "none"; // Hide the video if it fails to load
            console.error("Failed to load video:", e.target.src);
          }}>
          {safariAgent || img?.mp4Src ? (
            <source src={img?.mp4Src} type="video/mp4" />
          ) : (
            <source src={img?.src || img?.mp4Src} type="video/webm" />
          )}
          Your browser does not support the video tag.
        </video>
      );
    }

    return (
      <Image
        src={img?.src}
        width={img?.width || "100%"}
        height={img?.height || "auto"}
        className={imageClassName}
        loading="lazy"
      />
    );
  };

  return (
    <div
      className={`flex flex-wrap justify-between ${
        alignStart ? "items-start" : "items-center"
      } flex-col-reverse md:flex-row panel`}>
      <div className="w-full md:w-1/2 mt-[24px] md:mt-0">{renderMedia()}</div>
      <div className={`w-full md:w-6/12 ${contentClassName}`}>
        {headingType === "heading" ? (
          <h2 className={headingClassNames}>{heading}</h2>
        ) : (
          <div className={headingClassNames}>{heading}</div>
        )}
        <div className="text-2.5sm md:text-md text-[#240253]">{body}</div>
        {footer}
      </div>
    </div>
  );
};

TabPanel.defaultProps = {
  img: {
    src: "",
    mp4Src: "",
    width: "",
    height: "",
  },
  imageClassName: "",
  contentClassName: "md:p-[20px] mt-0",
  heading: "",
  headingClass: "mb-[32px]",
  body: "",
  mobileVisibleHeading: true,
  alignStart: false,
  footer: "",
  headingType: "heading",
  isVideo: false,
};

TabPanel.propTypes = {
  img: PropTypes.shape({
    src: PropTypes.string,
    mp4Src: PropTypes.string,
    width: PropTypes.string,
    height: PropTypes.string,
    placeholder: PropTypes.string,
  }),
  mobileVisibleHeading: PropTypes.bool,
  imageClassName: PropTypes.string,
  contentClassName: PropTypes.string,
  alignStart: PropTypes.bool,
  heading: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  headingClass: PropTypes.string,
  body: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  footer: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  headingType: PropTypes.oneOf(["heading", "div"]),
  isVideo: PropTypes.bool,
};

export default memo(TabPanel);
